import React, {useRef, useEffect, useState} from 'react';
import {
  Box,
  Flex,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import {useAiChat} from './hooks/useAiChat';

export const AiChatBot = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [previousResponseId, setPreviousResponseId] = useState<
    string | undefined
  >();
  const [input, setInput] = useState('');

  const {messages, status, sendMessage} = useAiChat({
    path: 'chat',
    setPreviousResponseId: (responseId) => setPreviousResponseId(responseId),
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages]);

  // Keep input focused after form submission
  useEffect(() => {
    if (status === 'ready' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [status]);

  const isLoading = status === 'streaming';
  return (
    <Flex h="100%" w="100%">
      {/* Main Chat Area */}
      <Flex direction="column" flex="1">
        {/* Messages Area */}
        <Box flex="1" overflowY="auto" p={4}>
          <VStack spacing={4} align="stretch">
            {messages.length === 0 ? (
              <Text color="gray.500" textAlign="center" mt={8}>
                Start a conversation by typing a message below.
              </Text>
            ) : (
              messages.map((message) => (
                <Box
                  key={message.id}
                  alignSelf={
                    message.role === 'user' ? 'flex-end' : 'flex-start'
                  }
                  maxW="70%"
                >
                  <Box
                    bg={message.role === 'user' ? 'blue.500' : 'gray.100'}
                    color={message.role === 'user' ? 'white' : 'black'}
                    p={3}
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    {message.parts.map((part, index) => (
                      <Text key={index} whiteSpace="pre-wrap">
                        {part.type === 'text' ? part.text : null}
                      </Text>
                    ))}{' '}
                    <Text
                      fontSize="xs"
                      color={message.role === 'user' ? 'blue.100' : 'gray.500'}
                      mt={1}
                    >
                      {new Date().toLocaleTimeString()}
                    </Text>
                  </Box>
                </Box>
              ))
            )}
            {isLoading && (
              <Box alignSelf="flex-start" maxW="70%">
                <Box bg="gray.100" p={3} borderRadius="lg" boxShadow="sm">
                  <HStack spacing={2}>
                    <Spinner size="sm" />
                    <Text>AI is thinking...</Text>
                  </HStack>
                </Box>
              </Box>
            )}
            {/* Invisible div to scroll to */}
            <div ref={messagesEndRef} />
          </VStack>
        </Box>

        {/* Input Area */}
        <Box p={4} borderTop="1px" borderColor="gray.200" bg="white">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage({text: input}, {body: {previousResponseId}});
              setInput('');
            }}
          >
            <HStack spacing={3}>
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                isDisabled={isLoading}
                size="lg"
                borderRadius="lg"
              />
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                isDisabled={!input.trim() || isLoading}
                borderRadius="lg"
              >
                Send
              </Button>
            </HStack>
          </form>
        </Box>
      </Flex>
    </Flex>
  );
};
